import { forwardRef, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  adminClientsService,
  type ClientDetail,
  type ClientIdentityPatch,
} from '../../../services/adminClientsService';
import { toastSuccess, toastError } from '../../../lib/toast';

export interface AutonomousTabHandle {
  getIsDirty: () => boolean;
  reset: () => void;
}

interface Props {
  clientId: string;
  initialData: ClientDetail;
  onSaved?: (updated: ClientDetail) => void;
}

interface LocalState {
  name: string;
  is_active: boolean;
}

const toLocal = (c: ClientDetail): LocalState => ({
  name: c.name,
  is_active: c.is_active,
});

export const IdentityTab = forwardRef<AutonomousTabHandle, Props>(
  ({ clientId, initialData, onSaved }, ref) => {
    const navigate = useNavigate();
    const [state, setState] = useState<LocalState>(() => toLocal(initialData));
    const [baseline, setBaseline] = useState<LocalState>(() => toLocal(initialData));
    const [saving, setSaving] = useState(false);
    const [hardDeleteOpen, setHardDeleteOpen] = useState(false);
    const [hardDeleteConfirm, setHardDeleteConfirm] = useState('');
    const [hardDeleting, setHardDeleting] = useState(false);

    // Bouton "Supprimer définitivement" visible uniquement quand le tenant est
    // déjà persisté comme inactif (baseline.is_active = false, pas state). Le
    // backend refusera 409 si encore actif OU s'il contient des données métier.
    const isSystem = initialData.is_system_tenant === true;
    const canHardDelete = !baseline.is_active && !isSystem;

    const isDirty = JSON.stringify(state) !== JSON.stringify(baseline);

    useImperativeHandle(ref, () => ({
      getIsDirty: () => isDirty,
      reset: () => setState(baseline),
    }));

    const handleHardDelete = async () => {
      setHardDeleting(true);
      try {
        await adminClientsService.hardDeleteClient(clientId);
        toastSuccess(`Client "${initialData.slug}" supprimé définitivement`);
        navigate('/parametres/clients');
      } catch (err) {
        toastError('Suppression échouée', err);
        setHardDeleting(false);
      }
    };

    const handleSave = async () => {
      if (!isDirty) return;
      setSaving(true);
      try {
        const payload: ClientIdentityPatch = {};
        if (state.name !== baseline.name) payload.name = state.name;
        if (state.is_active !== baseline.is_active) payload.is_active = state.is_active;
        const updated = await adminClientsService.patchIdentity(clientId, payload);
        setBaseline(toLocal(updated));
        setState(toLocal(updated));
        toastSuccess('Identité enregistrée');
        onSaved?.(updated);
      } catch (err) {
        toastError('Enregistrement échoué', err);
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="space-y-4 max-w-2xl">
        {isSystem && (
          <div className="rounded bg-purple-50 border border-purple-200 px-3 py-2 text-sm text-purple-900">
            Tenant système — utilisé pour le stockage interne (GT, benchmark). La
            désactivation et la suppression ne sont pas autorisées.
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
          <input
            type="text"
            value={state.name}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Slug <span className="text-xs text-gray-400">(non modifiable)</span>
          </label>
          <input
            type="text"
            value={initialData.slug}
            readOnly
            disabled
            title="Le slug n'est pas modifiable après création"
            className="w-full border border-gray-200 bg-gray-100 rounded px-3 py-2 text-sm font-mono text-gray-500 cursor-not-allowed"
          />
        </div>
        <div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={state.is_active}
              disabled={isSystem}
              onChange={(e) => setState((s) => ({ ...s, is_active: e.target.checked }))}
            />
            <span className="text-sm font-medium text-gray-700">Client actif</span>
          </label>
        </div>

        {canHardDelete && (
          <div className="mt-8 border-t border-red-200 pt-4">
            <h3 className="text-sm font-semibold text-red-700 mb-2">Zone dangereuse</h3>
            <p className="text-xs text-gray-600 mb-3">
              Le client est désactivé. Vous pouvez le supprimer définitivement s'il ne
              contient aucune donnée (documents, extractions, utilisateurs rattachés).
              Cette action est irréversible.
            </p>
            <button
              type="button"
              onClick={() => {
                setHardDeleteConfirm('');
                setHardDeleteOpen(true);
              }}
              className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50"
            >
              Supprimer définitivement
            </button>
          </div>
        )}

        <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-white border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving}
            className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>

        {hardDeleteOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded shadow-lg p-6 max-w-md w-full">
              <h2 className="text-lg font-semibold text-red-700 mb-3">
                Supprimer définitivement le client ?
              </h2>
              <p className="text-sm text-gray-700 mb-3">
                Cette action est <strong>irréversible</strong>. Le client{' '}
                <code className="bg-gray-100 px-1 rounded">{initialData.slug}</code> et
                toutes ses configurations (domaines, configs actives, rattachements
                utilisateurs) seront supprimés.
              </p>
              <p className="text-sm text-gray-700 mb-3">
                Si le client contient des données métier (documents, extractions, etc.),
                la suppression sera refusée par le serveur.
              </p>
              <label className="block text-xs text-gray-600 mb-1">
                Tapez le slug <code className="bg-gray-100 px-1 rounded">{initialData.slug}</code> pour confirmer :
              </label>
              <input
                type="text"
                value={hardDeleteConfirm}
                onChange={(e) => setHardDeleteConfirm(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono mb-4"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setHardDeleteOpen(false)}
                  disabled={hardDeleting}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleHardDelete}
                  disabled={hardDeleting || hardDeleteConfirm !== initialData.slug}
                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {hardDeleting ? 'Suppression...' : 'Supprimer définitivement'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

IdentityTab.displayName = 'IdentityTab';

export default IdentityTab;
